import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api';
import { PageHeader, Card, Button, Table, LoadingSpinner, Badge, StatusBadge } from '../components/UI';
import Pagination from '../components/Pagination';
import Select from '../components/Select';
import * as I from 'lucide-react';
import debounce from 'lodash/debounce';

const InvoiceSearchPage = ({ navigate, setSelectedInvoice }) => {
    const [invoices, setInvoices] = useState([]);
    const [vendorMap, setVendorMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');

    // Pagination state
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalInvoices, setTotalInvoices] = useState(0);
    const [pageSize] = useState(10); // Set to 10 invoices per page

    // --- Fetch Vendor Names ---
    const fetchVendorNames = useCallback(async (vendorIds) => {
        try {
            const uniqueVendorIds = [...new Set(vendorIds.filter(id => id && !vendorMap[id]))];

            if (uniqueVendorIds.length === 0) {
                return;
            }

            const token = localStorage.getItem('token');
            if (!token) {
                console.error('No authentication token found');
                return;
            }

            const config = {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            };

            try {
                const response = await api.get('/vendors', config);

                if (response.data && Array.isArray(response.data.content)) {
                    const vendorMapFromResponse = response.data.content.reduce((acc, vendor) => {
                        if (vendor.id && vendor.name) {
                            acc[vendor.id] = vendor.name;
                        }
                        return acc;
                    }, {});

                    const newVendorMap = {};
                    uniqueVendorIds.forEach(id => {
                        if (vendorMapFromResponse[id]) {
                            newVendorMap[id] = vendorMapFromResponse[id];
                        } else {
                            newVendorMap[id] = `Vendor ${id}`;
                        }
                    });

                    if (Object.keys(newVendorMap).length > 0) {
                        setVendorMap(prev => ({
                            ...prev,
                            ...newVendorMap
                        }));
                    }
                } else {
                    throw new Error('Invalid vendors response format');
                }
            } catch (error) {
                console.error('Error fetching vendors:', error?.response?.data || error.message);

                const vendorPromises = uniqueVendorIds.map(id =>
                    api.get(`/vendors/${id}`, config)
                        .then(response => ({
                            id: id.toString(),
                            name: response.data?.name || `Vendor ${id}`
                        }))
                        .catch(() => ({
                            id: id.toString(),
                            name: `Vendor ${id}`
                        }))
                );

                const vendorResults = await Promise.all(vendorPromises);
                const fallbackVendorMap = {};

                vendorResults.forEach(({ id, name }) => {
                    fallbackVendorMap[id] = name;
                });

                if (Object.keys(fallbackVendorMap).length > 0) {
                    setVendorMap(prev => ({
                        ...prev,
                        ...fallbackVendorMap
                    }));
                }
            }
        } catch (err) {
            console.error('A critical error occurred in fetchVendorNames:', err);
        }
    }, [vendorMap]);

    // --- Helper Functions ---
    const formatCurrency = (amount) => {
        if (amount == null) return 'N/A';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount);
    };


    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Invalid Date';
            const day = date.getDate();
            let daySuffix = 'th';
            if (day === 1 || day === 21 || day === 31) daySuffix = 'st';
            else if (day === 2 || day === 22) daySuffix = 'nd';
            else if (day === 3 || day === 23) daySuffix = 'rd';
            return `${day}${daySuffix} ${date.toLocaleDateString('en-IN', { month: 'short' })} ${date.getFullYear()}`;
        } catch (e) {
            console.warn('Invalid date format:', dateString);
            return 'Invalid Date';
        }
    };

    const getInvoiceAmount = (invoice) =>
        invoice?.totalAmount ?? invoice?.total ?? invoice?.amount ?? null;
    const getInvoiceNumber = (invoice) =>
        invoice?.invoiceNumber ?? invoice?.number ?? invoice?.id?.toString() ?? null;
    const getVendorId = (invoice) =>
        invoice?.vendorId ?? invoice?.vendor?.id ?? null;
    const getStatus = (invoice) =>
        invoice?.status ?? invoice?.state ?? null;
    const getInvoiceDate = (invoice) =>
        invoice?.invoiceDate ?? invoice?.date ?? null;
    const getDateReceived = (invoice) =>
        invoice?.dateReceived ?? invoice?.receivedDateTime ?? invoice?.createdDate ?? null;
    const getSubtotal = (invoice) =>
        invoice?.subtotal ?? null;

    // TDS helpers
    const getTdsAmount = (invoice) => invoice?.tdsAmount ?? null;
    const getTdsPercentage = (invoice) => invoice?.tdsPercentage ?? null;
    const getTdsRule = (invoice) => invoice?.tdsRule ?? null;
    const getInvoicePayable = (invoice) => invoice?.invoicePayable ?? null;

    // --- Fetch Invoices ---
    const fetchInvoices = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setError('Authentication required.');
                setLoading(false);
                return;
            }

            const params = new URLSearchParams();
            params.append('page', currentPage);
            params.append('size', pageSize);

            if (filterStatus && filterStatus !== 'all') {
                params.append('status', filterStatus.toUpperCase());
            }
            if (debouncedSearchTerm) {
                params.append('searchTerm', debouncedSearchTerm);
            }

            params.append('sort', 'dateReceived,desc');
            params.append('sort', 'id,desc');

            const config = { headers: { 'Authorization': `Bearer ${token}` } };
            const response = await api.get(`/invoices?${params.toString()}`, config);

            const responseData = response.data;
            console.log('API Response:', responseData); // Debug log

            // The backend returns a paginated response with content, totalElements, and totalPages
            const items = responseData.content || [];
            const totalElements = responseData.totalElements || 0;
            const totalPages = responseData.totalPages || 1;

            const fetchedInvoices = items.map(inv => ({
                ...inv,
                displayStatus: inv.status ? inv.status.toUpperCase() : 'UNKNOWN',
                totalAmount: getInvoiceAmount(inv) ?? 0,
                invoiceNumber: getInvoiceNumber(inv) ?? 'N/A',
                vendorId: getVendorId(inv) ?? null,
                invoiceDate: getInvoiceDate(inv) ?? null,
                dateReceived: inv.dateReceived ?? inv.receivedDateTime ?? null,
                subtotal: inv.subtotal ?? null
            }));

            console.log('Processed Invoices:', fetchedInvoices.length, 'Total Pages:', totalPages, 'Total Elements:', totalElements); // Debug log

            setInvoices(fetchedInvoices);
            setTotalInvoices(totalElements);
            setTotalPages(totalPages);
        } catch (err) {
            console.error('Fetch Invoices Error:', err);
            let msg = 'Failed to fetch invoices.';
            if (err.response?.status === 401) msg = 'Authentication failed. Please log in again.';
            else if (err.response?.status === 403) msg = 'Access Denied.';
            else msg = err.response?.data?.message || err.message || msg;
            setError(msg);
            setInvoices([]);
            setTotalInvoices(0);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, filterStatus, debouncedSearchTerm]);

    // Debounce search term effect
    const debouncedSearchHandler = useMemo(
        () =>
            debounce((term) => {
                setCurrentPage(0);
                setDebouncedSearchTerm(term.trim());
            }, 500),
        []
    );

    // Effect to call debounced search
    useEffect(() => {
        debouncedSearchHandler(searchTerm);
        return () => {
            debouncedSearchHandler.cancel();
        };
    }, [searchTerm, debouncedSearchHandler]);

    // Effect to trigger invoice fetch
    useEffect(() => {
        fetchInvoices();
    }, [fetchInvoices]);

    // Effect to trigger vendor fetch *after* invoices are loaded
    useEffect(() => {
        const vendorIds = invoices.map(inv => inv.vendorId).filter(Boolean);
        if (vendorIds.length > 0) {
            fetchVendorNames(vendorIds);
        }
    }, [invoices, fetchVendorNames]);

    // --- Event Handlers ---
    const handleSearchChange = (e) => setSearchTerm(e.target.value);
    const handleClearSearch = () => {
        setSearchTerm('');
    };
    const handleFilterChange = (e) => {
        setCurrentPage(0);
        setFilterStatus(e.target.value);
    };
    const handlePageChange = (newPage) => {
        // The Pagination component uses 1-based indexing
        // Convert to 0-based for our API
        setCurrentPage(newPage - 1);

        // Scroll to top of the page when changing pages
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleViewInvoice = (invoice) => {
        if (typeof setSelectedInvoice === 'function') {
            const vendorName = vendorMap[invoice.vendorId] || (invoice.vendorId ? `ID: ${invoice.vendorId}` : 'N/A');
            setSelectedInvoice({
                ...invoice,
                vendorName
            });
        }
        if (typeof navigate === 'function') {
            navigate('invoice-detail');
        }
    };

    // --- Table Data ---
    const tableHeaders = [
        'Invoice #',
        'Date Recvd',
        'Invoice Date',
        'Vendor Name',
        'Subtotal',
        'Total',
        'Invoice Status',
        'Actions'
    ];

    const tableData = invoices.map(invoice => {
        const vendorName =
            vendorMap[invoice.vendorId] ||
            (invoice.vendorId ? `ID: ${invoice.vendorId}` : 'N/A');
        return [
            getInvoiceNumber(invoice) || 'N/A',
            formatDate(getDateReceived(invoice)),
            formatDate(getInvoiceDate(invoice)),
            vendorName,
            formatCurrency(getSubtotal(invoice)),
            formatCurrency(getInvoiceAmount(invoice)),
            <StatusBadge key={`status-${invoice.id}`} status={getStatus(invoice)} />,
            <div className="flex space-x-2" key={invoice.id}>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleViewInvoice(invoice)}
                    title="View Invoice Details"
                >
                    <I.Eye className="w-4 h-4 mr-1" /> View
                </Button>
            </div>
        ];
    });

    const filterOptions = [
        { value: 'all', label: 'All Statuses' },
        { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
        { value: 'APPROVED', label: 'Approved' },
        { value: 'REJECTED', label: 'Rejected' },
        { value: 'INVOICE_PAID', label: 'Invoice Paid' },
        { value: 'TDS_PENDING', label: 'TDS Pending' },
        { value: 'TDS_PAID', label: 'TDS Paid' },
        { value: 'COMPLETED', label: 'Completed' }
    ];

    return (
        <div className="p-4 sm:p-6 lg:p-8 space-y-6 bg-gray-50 min-h-screen">
            <PageHeader
                title="Invoice Management"
                subtitle="Search and view invoices across all vendors and statuses"
                actions={[
                    <Button
                        key="refresh"
                        variant="outline"
                        onClick={() => fetchInvoices()}
                        disabled={loading}
                    >
                        <I.RefreshCw
                            className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`}
                        />{' '}
                        Refresh
                    </Button>
                ]}
            />

            <Card className="shadow-sm">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-4 border-b border-gray-200">
                    <div className="flex-1 min-w-0 relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                            <I.Search className="w-4 h-4 text-gray-400" />
                        </span>
                        <input
                            type="text"
                            placeholder="Search by Invoice # or Vendor Name..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            className="w-full pl-10 pr-10 py-2 h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                onClick={handleClearSearch}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 z-10"
                                title="Clear search"
                            >
                                <I.X className="h-4 w-4" />
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-3 flex-shrink-0 w-full lg:w-auto">
                        <div className="w-full lg:w-auto lg:min-w-[200px]">
                            <Select
                                id="statusFilter"
                                value={filterStatus}
                                onChange={handleFilterChange}
                                leftIcon={<I.Filter className="w-4 h-4 text-gray-400" />}
                                options={filterOptions}
                            />
                        </div>
                        {(searchTerm || filterStatus !== 'all') && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    handleClearSearch();
                                    setFilterStatus('all');
                                    setCurrentPage(0);
                                }}
                                title="Clear search and filter"
                            >
                                <I.X className="w-4 h-4 mr-1" /> Clear
                            </Button>
                        )}
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {loading && invoices.length === 0 ? (
                        <div className="flex items-center justify-center py-16">
                            <LoadingSpinner size="lg" />
                            <span className="ml-3 text-lg text-gray-500">
                                Loading invoices...
                            </span>
                        </div>
                    ) : invoices.length === 0 ? (
                        <div className="text-center py-16 px-4">
                            <I.SearchX className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                            <h3 className="text-lg font-semibold text-gray-700 mb-2">
                                No Invoices Found
                            </h3>
                            <p className="text-gray-500 text-sm">
                                {searchTerm || filterStatus !== 'all'
                                    ? 'No invoices match your current search or filter criteria.'
                                    : 'There are no invoices available.'}
                            </p>
                        </div>
                    ) : (
                        <Table headers={tableHeaders} data={tableData} loading={loading} />
                    )}
                </div>

                {totalPages > 1 && !error && invoices.length > 0 ? (
                    <div className="px-4 py-3 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-2">
                        <div className="text-xs sm:text-sm text-gray-500">
                            Showing <span className="font-medium">
                                {totalInvoices > 0 ? currentPage * pageSize + 1 : 0}
                            </span>
                            -
                            <span className="font-medium">
                                {Math.min((currentPage + 1) * pageSize, totalInvoices)}
                            </span>{' '}
                            of <span className="font-medium">{totalInvoices}</span> results
                        </div>
                        {totalPages > 1 && (
                            <Pagination
                                currentPage={currentPage + 1}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                                className="mt-2 sm:mt-0"
                            />
                        )}
                    </div>
                ) : null}
            </Card>
        </div>
    );
};

export default InvoiceSearchPage;


